import { create } from 'zustand';
import { supabase } from '@/lib/supabase';
import type { Identity } from '@/lib/types';

/**
 * Identity store
 *
 * Single source of truth for the authenticated user's identity, role,
 * organization, and flags. Populated by AuthInitializer after sign-in (or
 * on app mount when the session is resumed).
 *
 * Per Phase 1 "Never Again" rules:
 *  - The frontend never derives role from email, URL, local storage, or any
 *    client-side state. It reads it from the server via app_identity().
 *  - Components must not write `role` (or any other server-derived field)
 *    directly. The lint config bans direct mutation outside this file.
 *
 * Status:
 *  - 'unknown'       initial; AuthInitializer hasn't run yet
 *  - 'loading'       request in flight to app_identity()
 *  - 'authenticated' identity loaded successfully; `identity` is non-null
 *  - 'guest'         no Supabase session
 *  - 'error'         identity RPC failed; fail-closed (treat as guest for routing)
 */

export type IdentityStatus =
  | 'unknown'
  | 'loading'
  | 'authenticated'
  | 'guest'
  | 'error';

interface IdentityState {
  status: IdentityStatus;
  identity: Identity | null;
  error: string | null;

  /** Internal: AuthInitializer calls these. */
  _setLoading: () => void;
  _setAuthenticated: (identity: Identity) => void;
  _setGuest: () => void;
  _setError: (message: string) => void;
  _reset: () => void;

  /**
   * Public: re-fetch the identity blob from the server. Useful after a state
   * change that the auth events don't catch (e.g. invite acceptance flips
   * role / organization_id while the JWT is unchanged).
   */
  refresh: () => Promise<void>;
}

export const useIdentity = create<IdentityState>((set) => ({
  status: 'unknown',
  identity: null,
  error: null,

  _setLoading: () => set({ status: 'loading', error: null }),
  _setAuthenticated: (identity) =>
    set({ status: 'authenticated', identity, error: null }),
  _setGuest: () => set({ status: 'guest', identity: null, error: null }),
  _setError: (message) =>
    set({ status: 'error', identity: null, error: message }),
  _reset: () => set({ status: 'unknown', identity: null, error: null }),

  async refresh() {
    set({ status: 'loading', error: null });
    const { data: sessionData } = await supabase.auth.getSession();
    if (!sessionData.session) {
      set({ status: 'guest', identity: null });
      return;
    }
    const { data, error } = await supabase.rpc('app_identity');
    if (error) {
      set({ status: 'error', identity: null, error: error.message });
      return;
    }
    set({ status: 'authenticated', identity: data as Identity, error: null });
  },
}));

/**
 * Convenience selectors. Components use these instead of reading the raw store.
 *
 * Example:
 *   const role = useRole();           // 'owner' | 'manager' | 'staff' | 'system_admin' | null
 *   const orgName = useOrgName();     // string | null
 */
export const useRole = () => useIdentity((s) => s.identity?.role ?? null);

export const useOrg = () => useIdentity((s) => s.identity?.organization ?? null);

export const useUser = () => useIdentity((s) => s.identity?.user ?? null);

export const useFlags = () => useIdentity((s) => s.identity?.flags ?? null);

/** Day 15.x — current org subscription tier (standard | pro | enterprise | trial). */
export const useTier = () => useIdentity((s) => s.identity?.license?.tier ?? 'trial');

/** Day 15.x — true when caller is entitled to the pay-rates / labor-cost feature. */
export const useHasPayRates = () =>
  useIdentity((s) => Boolean(s.identity?.feature_flags?.has_pay_rates));

export const useIsAuthenticated = () =>
  useIdentity((s) => s.status === 'authenticated');
